<template>
	<Browser
		ref="browserRef"
		v-model="searchValue"
		:is-x-s="isXS"
		:placeholder="placeholder"
		:label="label"
		:hide-error-box="hideErrorBox"
		:error="error"
		:size="size"
		:keep-selected="keepSelected"
		:helper-text="helperText"
		:min-length="minSearchLength"
		@browse="browse"
		@clear="clearPredictions"
		@focusout="onFocusOut"
		@enter="search"
	>
		<template v-if="searchFromGoogle" #prefix>
			<slot name="prefix"><div></div></slot>
		</template>
		<template v-if="searchFromGoogle" #suffix>
			<div class="d-flex">
				<PButton
					v-if="showClearButton || searchValue"
					type="text"
					color="neutral"
					@click="clearSearch"
				>
					<Icon icon="close" radius="20px" />
				</PButton>
				<PButton type="text" color="neutral" @click="search">
					<Icon icon="search" radius="20px" />
				</PButton>
			</div>
		</template>
		<GoogleResultList
			:predictions="predictions"
			:search-text="searchValue"
			:search-from-start="searchFromStart"
			:is-loading="isLoading"
			:min-length="minSearchLength"
			:show-all-results-button="showAllResultsButton"
			@select="selectPlace"
			@show-all-results="search"
		>
			<template #emptyState>
				<slot name="emptyState" />
			</template>
		</GoogleResultList>
	</Browser>
</template>

<script setup lang="ts">
	import { ref, watch } from 'vue'
	import { GooglePlace } from '~/models/GPlace'
	import { Spot2 } from '~/models/Spot'
	import { SpotService } from '~/passporter-services/spot/service'
	import { DestinationService } from '~/passporter-services/destination/service'
	import { Destination2 } from '~/models/Destination'
	import { BrowserService } from '~/services/browser/service'
	import EventsService from '~/services/events'
	import { useI18n } from 'vue-i18n'
	import { Place } from '~/models/Place'
	import { TripSpotActions } from '~/actions/trip/spot'

	const { t, locale } = useI18n()

	interface Props {
		isXS: boolean
		types?: string[]
		label?: string
		keepSelected?: boolean
		hideErrorBox?: boolean
		error?: string
		size?: 's' | 'm' | 'l'
		placeholder?: string
		searchFromGoogle?: boolean
		showClearButton?: boolean
		searchFromStart?: boolean
		helperText?: string
		minSearchLength?: number
		showAllResultsButton?: boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		types: (): string[] => [],
		label: '',
		error: '',
		size: 'm',
		placeholder: '',
		helperText: '',
		minSearchLength: 3,
	})
	const emit = defineEmits(['select', 'enter', 'search', 'clear', 'click-result', 'focusout'])

	const searchValue = defineModel<string>()
	const browserRef = ref()
	const predictions = ref<GooglePlace[]>([])
	const selectedPlace = ref<Spot2 | Destination2 | Place>()
	const isLoading = ref<boolean>(false)
	const isSelecting = ref<boolean>(false)

	watch(
		() => searchValue.value,
		(value) => {
			if (isSelecting.value) return
			const length = value?.length ?? 0
			if (!value || length < props.minSearchLength) {
				isLoading.value = false
				return
			}
			isLoading.value = true
		}
	)

	const browse = async (text: string) => {
		if (!text) return clearPredictions()
		let types
		if (props.types) types = props.types as string[]
		isLoading.value = true
		predictions.value = await BrowserService.getPlacesPredictions(text, types, locale.value)
		isLoading.value = false
	}

	const selectPlace = async (prediction: GooglePlace) => {
		emit('click-result')
		let place
		if (props.searchFromGoogle) {
			place = await getGooglePlace(prediction.placeId)
		} else {
			place = await getPlace(prediction)
		}
		if (!place) return EventsService.emitError(t('error_destination_not_found'))
		selectedPlace.value = place
		isSelecting.value = true
		searchValue.value = props.keepSelected ? place.name : ''
		isSelecting.value = false
		emit('select', place)
	}

	const getPlace = async (prediction: GooglePlace) => {
		let place: Spot2 | Destination2 | undefined
		try {
			place = await getDestination(prediction.placeId)
		} catch {
			place = await getSpot(prediction.placeId)
		}
		return place
	}

	const getGooglePlace = async (placeId: string): Promise<Place | undefined> => {
		return await TripSpotActions.getPlace(placeId)
	}

	const clearPredictions = () => {
		predictions.value = []
		isLoading.value = false
	}

	const getDestination = async (placeId: string) => {
		return await DestinationService.find(placeId)
	}

	const getSpot = async (placeId: string) => {
		let spot: Spot2 | undefined
		try {
			spot = await SpotService.find(placeId)
		} catch {
			/* empty */
		}
		return spot
	}

	const onFocusOut = () => {
		if (!props.keepSelected) {
			searchValue.value = ''
			return
		}
		if (props.searchFromGoogle) return

		if (selectedPlace.value) searchValue.value = selectedPlace.value.name
		if (!searchValue.value) {
			searchValue.value = ''
			emit('select', {})
		}
		emit('focusout')
	}

	const search = () => {
		browserRef.value?.closeDialog()
		browserRef.value?.closeResults()
		emit('search', searchValue.value)
		clearPredictions()
	}

	const clearSearch = () => {
		searchValue.value = ''
		clearPredictions()
		emit('clear')
	}
</script>
